import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';
import { Check, Loader2, Package, Pill } from 'lucide-react';

const BottomSaleBar = ({
  cart,
  supplementCart,
  consumablesCart,
  cartTotal,
  supplementCartTotal,
  grandTotal,
  ticketMediciel,
  setTicketMediciel,
  scheduleRenewals,
  setScheduleRenewals,
  followUpDate,
  setFollowUpDate,
  followUpReason,
  setFollowUpReason,
  handleValidateSale,
  submitting,
  language
}) => {
  if (cart.length === 0 && supplementCart.length === 0 && consumablesCart.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-[calc(70px+env(safe-area-inset-bottom,0px))] sm:bottom-[70px] left-0 right-0 bg-white border-t border-[#E7E5E4] p-3 sm:p-4 z-40 shadow-lg">
      <div className="max-w-7xl mx-auto space-y-2 sm:space-y-3">
        {/* Follow-up Options */}
        <div className="flex flex-wrap gap-3 pb-3 border-b border-[#E7E5E4]">
          {/* Auto renewal for supplements */}
          {supplementCart.length > 0 && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="scheduleRenewals"
                checked={scheduleRenewals}
                onChange={(e) => setScheduleRenewals(e.target.checked)}
                className="w-4 h-4 rounded border-green-300 text-green-500 focus:ring-green-500"
              />
              <Label htmlFor="scheduleRenewals" className="text-xs text-green-700 cursor-pointer">
                {language === 'fr' ? 'Relance auto renouvellement cure' : 'Auto renewal reminder'}
              </Label>
            </div>
          )}
          
          {/* Follow-up Date */}
          <div className="flex items-center gap-2">
            <Label className="text-xs text-[#78716C] whitespace-nowrap">
              {language === 'fr' ? 'RDV suivi:' : 'Follow-up:'}
            </Label>
            <Input
              type="date"
              value={followUpDate}
              onChange={(e) => setFollowUpDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-36 h-8 text-xs"
              placeholder={language === 'fr' ? 'Optionnel' : 'Optional'}
            />
          </div>
          
          {followUpDate && (
            <Select value={followUpReason} onValueChange={setFollowUpReason}>
              <SelectTrigger className="w-32 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="check_up">{language === 'fr' ? 'Suivi routine' : 'Check-up'}</SelectItem>
                <SelectItem value="renewal">{language === 'fr' ? 'Renouvellement' : 'Renewal'}</SelectItem>
                <SelectItem value="custom">{language === 'fr' ? 'Autre' : 'Other'}</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
        
        {/* Totals breakdown */}
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1">
            {cart.length > 0 && (
              <p className="text-xs text-[#78716C]">
                <Package className="w-3 h-3 inline mr-1" />
                Cosmétiques: {cartTotal.toLocaleString('fr-FR')} FCFA
              </p>
            )}
            {supplementCart.length > 0 && (
              <p className="text-xs text-green-600">
                <Pill className="w-3 h-3 inline mr-1" />
                Compléments: {supplementCartTotal.toLocaleString('fr-FR')} FCFA
              </p>
            )}
            <p className="text-xl font-bold text-[#0F172A]">
              {grandTotal.toLocaleString('fr-FR')} FCFA
            </p>
          </div>
          
          {/* Ticket Mediciel Input */}
          <div className="flex-1 max-w-xs">
            <Label htmlFor="ticket-mediciel" className="text-xs font-medium text-[#5D4E37]">
              N° Ticket Mediciel <span className="text-red-500">*</span>
            </Label>
            <Input
              id="ticket-mediciel"
              value={ticketMediciel}
              onChange={(e) => setTicketMediciel(e.target.value)}
              placeholder="Ex: TK-12345"
              className="h-10 border-[#C9A086] focus:border-[#10B981]"
              data-testid="ticket-mediciel-input"
            />
          </div>
          
          <Button
            onClick={handleValidateSale}
            disabled={submitting || !ticketMediciel.trim()}
            className="flex-1 max-w-xs bg-[#10B981] hover:bg-[#059669] text-white h-12 rounded-full disabled:bg-gray-400"
            data-testid="validate-sale-btn"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Check className="w-5 h-5 mr-2" />
                {language === 'fr' ? 'Valider la vente' : 'Validate Sale'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BottomSaleBar;
